import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';

const prismProgressIndicatorCss = ":host{display:block;color:var(--prism-color-theme-accent-primary);--_diameter:var(--prism-progress-indicator-diameter, 36px);--_stroke-width:var(--prism-progress-indicator-stroke-width, 4px);--_border-radius:var(--prism-progress-indicator-border-radius, var(--prism-border-radius-global-normal));--_background-color:var(--prism-progress-indicator-background-color, var(--prism-color-theme-surface))}:host([size=sm]){--_diameter:var(--prism-progress-indicator-diameter, 18px);--_stroke-width:var(--prism-progress-indicator-stroke-width, 2px)}.pwc-indicator{width:100%;height:100%}:host([type=linear]){width:100%;height:var(--_stroke-width);background-color:var(--_background-color);border-radius:var(--_border-radius)}:host([type=linear]) .pwc-indicator{background-color:currentcolor;border-radius:var(--_border-radius)}:host([type=linear][mode=determinate]) .pwc-indicator{transform-origin:top left;transition:width 250ms 0ms ease-in-out;width:calc(var(--progress, 0) * 100%)}:host([type=linear][mode=indeterminate]){overflow-x:hidden}:host([type=linear][mode=indeterminate]) .pwc-indicator{animation:1.6s ease-in-out 1ms infinite pwc-progress-bar-linear-indeterminate}:host([type=circular]){width:var(--_diameter);height:var(--_diameter)}:host([type=circular]) svg{width:var(--_diameter);height:var(--_diameter);stroke-width:var(--_stroke-width);fill:transparent}:host([type=circular]) svg circle.pwc-progress-bar{stroke:currentcolor;stroke-dasharray:var(--circumference)}:host([type=circular][mode=determinate]) svg{rotate:-90deg}:host([type=circular][mode=determinate]) svg circle.pwc-progress-bar{--progress-bar-circumference:calc(var(--circumference) * (1 - var(--progress, 0)));stroke-dashoffset:var(--progress-bar-circumference);transition:stroke-dashoffset 500ms 0ms cubic-bezier(0, 0, 0.2, 1)}:host([type=circular][mode=determinate]) svg circle.pwc-background{stroke:var(--_background-color)}:host([type=circular][mode=indeterminate]) svg{animation:1.2s linear 1ms infinite pwc-progress-bar-circular-indeterminate}:host([type=circular][mode=indeterminate]) svg circle.pwc-progress-bar{stroke-dashoffset:calc(var(--circumference) / 4)}:host([type=circular][mode=indeterminate]) svg circle.pwc-background{display:none}@keyframes pwc-progress-bar-circular-indeterminate{0%{rotate:0}100%{rotate:360deg}}@keyframes pwc-progress-bar-linear-indeterminate{0%{transform:translateX(-100%)}50%{transform:translateX(0)}100%{transform:translateX(100%)}}";
const PrismProgressIndicatorStyle0 = prismProgressIndicatorCss;

const MIN_VALUE = 0;
const MAX_VALUE = 100;
const PrismProgressIndicator = /*@__PURE__*/ proxyCustomElement(class PrismProgressIndicator extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.type = 'linear';
        this.mode = 'indeterminate';
        this.value = 0;
        this.size = 'lg';
        this.radius = 0;
        this.circumference = 0;
    }
    /**
     * Update the radius and circumference of the circular indicator. This needs to be done after the DOM has been
     * set up, since the calculations are looking at the SVG element and CSS styling in order to get the correct values.
     */
    componentDidRender() {
        if (this.type !== 'circular')
            return;
        this.radius = this.calculateRadius();
        this.circumference = this.calculateCircumference();
    }
    render() {
        return (h(Host, { key: '7aac1261b28fa883ec55c05fa633e3b42b373b49', role: "progressbar", "aria-label": "progress bar", tabindex: "-1" // Prevent progress indicators from being focusable with keyboard navigation
            ,
            "aria-valuemin": MIN_VALUE, "aria-valuemax": MAX_VALUE, "aria-valuenow": this.mode === 'determinate' && this.clampedValue() }, h("div", { key: 'f5c6ef7c20725e44b22d59591cd95c599a8334fd', part: "indicator", class: "pwc-indicator", style: this.mode === 'determinate' && { ['--progress']: `${this.progressBarPercent()}` } }, h("slot", { key: '027eaa680d8966c77fa80944b46658ebfdb6c5e6', name: "custom-indicator" }), this.type === 'circular' && (h("svg", { key: 'c21aac00d5e26693eddc30c2f05ba47b6a9a465c', xmlns: "http://www.w3.org/2000/svg", focusable: "false", style: { ['--circumference']: `${this.circumference}px` } }, h("circle", { key: 'cac67a8bf0f5fb25824da7b9abc6b907d927c27b', cx: "50%", cy: "50%", r: this.radius, class: "pwc-background" }), h("circle", { key: 'f3eb457ed09b48457ed739f37f6c5d369a1cf7ac', cx: "50%", cy: "50%", r: this.radius, class: "pwc-progress-bar" }))))));
    }
    /**
     * Get the progress bar's percent value based on `value`, and the max value.
     *
     * @returns a number between 0 and 1
     */
    progressBarPercent() {
        return this.clampedValue() / MAX_VALUE;
    }
    /**
     * @returns `value` clamped between the minimum and maximum value of the progress indicator.
     */
    clampedValue() {
        return Math.max(MIN_VALUE, Math.min(this.value, MAX_VALUE));
    }
    /**
     * Calculate the correct radius of the circular indicator so that it fills the SVG viewport, taking into account the
     * thickness of the progress bar (i.e. stroke width).
     *
     * @returns the radius of the circular progress indicator, measured in pixels
     */
    calculateRadius() {
        const svgElement = this.hostElement.shadowRoot.querySelector('svg');
        if (!svgElement)
            return 0;
        const svgStyle = window.getComputedStyle(svgElement, null);
        const diameter = Number.parseFloat(svgStyle.width);
        const strokeWidth = Number.parseFloat(svgStyle.strokeWidth);
        if (Number.isNaN(diameter) || Number.isNaN(strokeWidth))
            return 0;
        return (diameter - strokeWidth) / 2;
    }
    /**
     * Calculate the circumference of the circular indicator.
     *
     * @returns the circumference of the circular progress indicator, measured in pixels
     */
    calculateCircumference() {
        return 2 * Math.PI * this.radius;
    }
    get hostElement() { return this; }
    static get style() { return PrismProgressIndicatorStyle0; }
}, [1, "prism-progress-indicator", {
        "type": [513],
        "mode": [513],
        "value": [2],
        "size": [513],
        "radius": [32],
        "circumference": [32]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["prism-progress-indicator"];
    components.forEach(tagName => { switch (tagName) {
        case "prism-progress-indicator":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, PrismProgressIndicator);
            }
            break;
    } });
}

export { PrismProgressIndicator as P, defineCustomElement as d };

